import '@/recoilEnv';
import '@/utils/clearSessionStorageOnReload';
import '@/whyDidYouRenderSetup';
import { AppLoadingProvider } from '@/providers/AppLoadingProvider';
import { AppLocaleProvider } from '@/providers/AppLocaleProvider';
import { AppSignalRProvider } from '@/providers/AppSignalRProvider';
import { PageTitleProvider } from '@/contexts/PageTitleContext';
import '@/styles/globals.css?v=25.1218.001';
import 'antd/dist/reset.css';
import ErrorBoundary from 'antd/es/alert/ErrorBoundary';
import Cookies from 'js-cookie';
import type { AppProps } from 'next/app';
import { Roboto } from 'next/font/google';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RecoilRoot, useSetRecoilState } from 'recoil';
import {
  HEADER_SCROLL_HIDE_STORAGE_KEY,
  headerScrollHideEnabledState,
} from '@/states/headerPreference';
import {
  buildLoginHref,
  buildLogoutHref,
  getReturnUrlFromPath,
  redirectToDashboardOnLoginFromOtherTab,
  redirectToLoginOnSessionLost,
  subscribeLoginAcrossTabs,
  subscribeLogoutAcrossTabs,
} from '@/utils/authCrossTabSync';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Layout } from '../components/Layout';
import { OpenTabTracker } from '@/components/Header/OpenTabTracker';
import { useAuthStore } from '../store/useAuthStore';
import { ROUTES } from '../utils/pathRoutes';
import { tokenService } from '../services/token/token-service';
import '@/components/MeasureIndicators/styles/Measure.css';
import '@/styles/ant-table-docked-scroll.css';
import { ConfigProvider } from 'antd';
import theme from '@/themeConfig';
import { UnsavedChangesProvider } from '@/contexts/UnsavedChangesContext';
import { AuthProvider, AuthUser } from '@/providers/AuthenProvider';
import type { AxiosError } from 'axios';
import { IDLE_FEATURE_ENABLED } from '@/constants/idle';
import { IdleGuard } from '@/components/Idle/IdleGuard';
import { clearIdleSession, isAppIdleActive } from '@/utils/idleSession';
import { TicketWidgetEmbed } from '@/components/common/TicketWidgetEmbed';
import { ToppionAIAssistant } from '@/components/common/ToppionAIAssistant';
import { isSurveyTakingRoute } from '@/utils/surveyTakingRoute';
import {
  enforceDbRestoreLockIfNeeded,
  subscribeDbRestoreLockPage,
} from '@/utils/dbRestoreSync';
import {
  dispatchForceAccountLogout,
  revalidateLoggedInAccount,
  shouldSkipAccountRevalidate,
} from '@/utils/revalidateLoggedInAccount';
const roboto = Roboto({
  subsets: ['latin'],
  style: ['normal', 'italic'],
});
import {
  normalizeRoutePath,
  resolveEffectivePathname,
  VanHoaAppPageRenderer,
  VanHoaPhanHeSync,
} from '@/components/PhanHe/VanHoaPhanHeHandler';
import { InitLoadingModal } from '@/components/InitLoadingModal/InitLoadingModal';

function DbRestoreLockGuard() {
  const router = useRouter();
  const hydrated = useAuthStore((state) => state.hydrated);
  const accessToken = useAuthStore((state) => state.accessToken);

  const checkDbRestoreLock = useCallback(() => {
    if (!hydrated) return;

    const path = router.asPath.split('?')[0] || '/';
    const lowerPath = path.toLowerCase();
    if (
      lowerPath.includes(ROUTES.LOGIN.toLowerCase())
      || lowerPath.includes(ROUTES.LOGOUT.toLowerCase())
      || lowerPath.includes(ROUTES.AUTH_SESSION_CHANGED.toLowerCase())
      || isSurveyTakingRoute(router.pathname, path)
    ) {
      return;
    }

    const isLoggedIn = Boolean(accessToken && !tokenService.isTokenExpired(accessToken));
    if (!isLoggedIn) return;

    enforceDbRestoreLockIfNeeded(path);
  }, [hydrated, accessToken, router.asPath, router.pathname]);

  useEffect(() => {
    checkDbRestoreLock();
  }, [checkDbRestoreLock]);

  useEffect(() => {
    const unsub = subscribeDbRestoreLockPage((phase) => {
      if (phase === 'restoring') {
        checkDbRestoreLock();
      }
    });
    return unsub;
  }, [checkDbRestoreLock]);

  useEffect(() => {
    const handleRouteChange = () => {
      checkDbRestoreLock();
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [checkDbRestoreLock, router.events]);

  return null;
}

function AuthCrossTabSync() {
  useEffect(() => {
    const isOnSessionChangedPage = () => {
      const path = window.location.pathname.toLowerCase();
      return path.includes(ROUTES.AUTH_SESSION_CHANGED.toLowerCase())
        || path.includes(ROUTES.DB_RESTORE_LOCK.toLowerCase());
    };

    const unsubLogout = subscribeLogoutAcrossTabs(() => {
      if (isOnSessionChangedPage()) return;
      void useAuthStore.getState().clearAuthAll().finally(() => {
        redirectToLoginOnSessionLost();
      });
    });
    const unsubLogin = subscribeLoginAcrossTabs(() => {
      redirectToDashboardOnLoginFromOtherTab();
    });
    return () => {
      unsubLogout();
      unsubLogin();
    };
  }, []);
  return null;
}

/**
 * Lắng nghe force logout event từ SignalR khi:
 * - Admin xóa user đang login
 * - Admin chuyển user sang trạng thái nghỉ việc
 * - Admin thay đổi role group của user đang login
 */
function ForceLogoutListener() {
  useEffect(() => {
    const handleForceLogout = (event: Event) => {
      const customEvent = event as CustomEvent<{ reason?: string; message?: string }>;
      const { reason = 'session_expired', message = 'Phiên đăng nhập đã kết thúc' } = customEvent.detail || {};

      console.log('[ForceLogoutListener] Received force logout event', { reason, message });

      // Hiển thị modal thông báo
      Modal.warning({
        title: 'Thông báo',
        icon: <ExclamationCircleOutlined />,
        content: message,
        okText: 'Đóng',
        onOk: () => {
          // Clear auth state
          const authStore = useAuthStore.getState();
          void authStore.clearAuthAll();

          // Redirect về login
          window.location.href = buildLoginHref('').replace('?returnUrl=', `?reason=${encodeURIComponent(reason)}&returnUrl=`);
        },
      });
    };

    window.addEventListener('forceUserLogout', handleForceLogout);
    return () => window.removeEventListener('forceUserLogout', handleForceLogout);
  }, []);
  return null;
}

function isBrowserReload(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const entry = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    return entry?.type === 'reload';
  } catch {
    const legacy = (performance as Performance & { navigation?: { type?: number } }).navigation;
    return legacy?.type === 1;
  }
}

/** Chỉ F5 / reload: gọi profile server, logout nếu NV đã bị xóa dù JWT còn hạn. */
function LoggedInAccountRevalidate() {
  const router = useRouter();
  const hydrated = useAuthStore((state) => state.hydrated);
  const accessToken = useAuthStore((state) => state.accessToken);
  const didRunRef = useRef(false);

  const shouldSkipPath = useCallback(() => {
    const path = (router.asPath.split('?')[0] || '/').toLowerCase();
    return (
      path.includes(ROUTES.LOGIN.toLowerCase())
      || path.includes(ROUTES.LOGOUT.toLowerCase())
      || path.includes(ROUTES.AUTH_SESSION_CHANGED.toLowerCase())
      || path.includes(ROUTES.DB_RESTORE_LOCK.toLowerCase())
      || isSurveyTakingRoute(router.pathname, path)
      || shouldSkipAccountRevalidate(router.pathname, path)
    );
  }, [router.asPath, router.pathname]);

  const runRevalidate = useCallback(async () => {
    if (!hydrated || shouldSkipPath()) return;
    if (!accessToken || tokenService.isTokenExpired(accessToken)) return;

    const result = await revalidateLoggedInAccount();
    if (result.status === 'invalid') {
      dispatchForceAccountLogout(result.reason, result.message);
    }
  }, [accessToken, hydrated, shouldSkipPath]);

  useEffect(() => {
    if (didRunRef.current) return;
    if (!isBrowserReload()) {
      didRunRef.current = true;
      return;
    }
    if (!hydrated) return;
    didRunRef.current = true;
    void runRevalidate();
  }, [hydrated, runRevalidate]);

  return null;
}

function RecoilHeaderPreferenceHydrate() {
  const setHeaderScrollHide = useSetRecoilState(headerScrollHideEnabledState);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HEADER_SCROLL_HIDE_STORAGE_KEY);
      if (raw === null) return;
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed === 'boolean') {
        setHeaderScrollHide(parsed);
      }
    } catch {
      // ignore invalid JSON
    }
  }, [setHeaderScrollHide]);
  return null;
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [isAuth, setAuth] = useState(false);
  const accessToken = useAuthStore((state) => state.accessToken);
  const hydrated = useAuthStore((state) => state.hydrated);
  const profileUser = useAuthStore((state) => state.profile);
  const browserPathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isSurveyTakingPage = isSurveyTakingRoute(router.pathname, browserPathname);
  
  // Init loading modal state
  const [initProgress, setInitProgress] = useState<{ phase: 'idle' | 'critical' | 'done'; message: string; percent: number }>({ phase: 'idle', message: '', percent: 0 });
  const [showInitModal, setShowInitModal] = useState(false);

  // Subscribe to init progress store
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const win = window as any;
    if (!win.__initProgress) return;
    
    const unsubscribe = win.__initProgress.subscribe((p: typeof initProgress) => {
      setInitProgress(p);
      if (p.phase === 'critical') {
        setShowInitModal(true);
      }
    });
    return unsubscribe;
  }, []);
  const isOnIdlePage =
    IDLE_FEATURE_ENABLED && router.asPath.toLowerCase().includes(ROUTES.IDLE.toLowerCase());
  const isOnAuthSessionChangedPage = router.asPath
    .toLowerCase()
    .includes(ROUTES.AUTH_SESSION_CHANGED.toLowerCase());
  const isOnDbRestoreLockPage = router.asPath
    .toLowerCase()
    .includes(ROUTES.DB_RESTORE_LOCK.toLowerCase());

  useEffect(() => {
    if (!IDLE_FEATURE_ENABLED) {
      clearIdleSession();
    }
  }, []);

  useEffect(() => {
    if (!hydrated) return; // ⛔ chỉ chạy khi đã hydrate

    const isOnLoginPage = router.asPath.toLowerCase().includes(ROUTES.LOGIN.toLowerCase());
    const isOnLogoutPage = router.asPath.toLowerCase().includes(ROUTES.LOGOUT.toLowerCase());
    const isLoggedIn = (accessToken && !tokenService.isTokenExpired(accessToken)) ? true : false;

    if (!isLoggedIn && !isOnLoginPage && !isOnLogoutPage && !isOnAuthSessionChangedPage && !isOnDbRestoreLockPage && !isSurveyTakingPage) {
        Cookies.remove('accessToken');
        const returnUrl = getReturnUrlFromPath(router.asPath);
        const href = accessToken == null ? buildLoginHref(returnUrl) : buildLogoutHref(returnUrl);
        window.location.replace(href);
    } else {
      setAuth(isLoggedIn);
    }
  }, [hydrated, accessToken, router, isSurveyTakingPage, isOnAuthSessionChangedPage]);

  useEffect(() => {
    if (!IDLE_FEATURE_ENABLED || !hydrated || !isAuth) return;

    const isOnLoginPage = router.asPath.toLowerCase().includes(ROUTES.LOGIN.toLowerCase());
    const isOnLogoutPage = router.asPath.toLowerCase().includes(ROUTES.LOGOUT.toLowerCase());
    if (isOnLoginPage || isOnLogoutPage || isOnAuthSessionChangedPage || isOnDbRestoreLockPage || isSurveyTakingPage) return;

    if (isAppIdleActive() && !isOnIdlePage) {
      void router.replace(ROUTES.IDLE);
    }
  }, [hydrated, isAuth, isOnIdlePage, isSurveyTakingPage, router, isOnAuthSessionChangedPage]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as AxiosError | Error | { cancelled?: boolean } | string | undefined;
      if (reason === 'Route change blocked by unsaved changes') {
        event.preventDefault();
        return;
      }
      if (
        reason &&
        typeof reason === 'object' &&
        'cancelled' in reason &&
        (reason as { cancelled?: boolean }).cancelled === true
      ) {
        event.preventDefault();
        return;
      }
      const isAxiosLike = Boolean(
        reason &&
          typeof reason === 'object' &&
          'isAxiosError' in reason &&
          (reason as AxiosError).isAxiosError,
      );
      if (!isAxiosLike) return;

      const axiosError = reason as AxiosError<{ message?: string }>;
      const status = axiosError.response?.status;
      const url = axiosError.config?.url || '';
      const msg =
        axiosError.response?.data?.message || axiosError.message || 'Unhandled Axios error';
      console.error('[Unhandled AxiosError]', { status, url, msg, method: axiosError.config?.method });
      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const isOnLoginPage = router.asPath.toLowerCase().includes(ROUTES.LOGIN.toLowerCase());
  const isOnLogoutPage = router.asPath.toLowerCase().includes(ROUTES.LOGOUT.toLowerCase());
  const shouldUseLayout =
      isAuth
      && !isOnLogoutPage
      && !isOnLoginPage
      && !isOnAuthSessionChangedPage
      && !isOnDbRestoreLockPage
      && !isSurveyTakingPage
      && !isOnIdlePage;
  const isLoggedInForIdle =
      IDLE_FEATURE_ENABLED
      && Boolean(accessToken && hydrated && !tokenService.isTokenExpired(accessToken));
  const shouldRunIdleGuard =
      isLoggedInForIdle
      && !isOnLoginPage
      && !isOnLogoutPage
      && !isOnAuthSessionChangedPage
      && !isOnDbRestoreLockPage
      && !isSurveyTakingPage
      && !isOnIdlePage;

  const [profilePhanHeSyncing, setProfilePhanHeSyncing] = useState(false);
  const handleProfilePhanHeSyncing = useCallback((syncing: boolean) => {
    setProfilePhanHeSyncing(syncing);
  }, []);

  const effectivePathname = useMemo(
    () => resolveEffectivePathname(router.pathname, router.asPath),
    [router.pathname, router.asPath],
  );
  const isIntroPage = effectivePathname === normalizeRoutePath(ROUTES.INTRO);

  return (
    <RecoilRoot>
      {/* Init Loading Modal - hiển thị khi đang khởi tạo dữ liệu */}
      <InitLoadingModal />
      <AuthCrossTabSync />
      <OpenTabTracker />
      <ForceLogoutListener />
      <LoggedInAccountRevalidate />
      <DbRestoreLockGuard />
      <VanHoaPhanHeSync
        pathname={router.pathname}
        asPath={router.asPath}
        onProfileSyncingChange={handleProfilePhanHeSyncing}
      />
      <RecoilHeaderPreferenceHydrate />
      <AppLocaleProvider>
        <AppLoadingProvider>
          <PageTitleProvider>
            <ErrorBoundary>
              <ConfigProvider theme={theme}>
                <div className={roboto.className}>
                  {shouldRunIdleGuard && <IdleGuard enabled />}
                  <TicketWidgetEmbed enabled={isAuth && !isOnLoginPage && !isOnLogoutPage} />
                  <ToppionAIAssistant enabled={isAuth && !isOnLoginPage && !isOnLogoutPage} />
                  {shouldUseLayout && (
                    <AppSignalRProvider>
                      <AuthProvider user={profileUser as AuthUser | null}>
                        <Layout isIntro={isIntroPage} profilePhanHeSyncing={profilePhanHeSyncing}>
                          <UnsavedChangesProvider>
                            <VanHoaAppPageRenderer
                              pathname={router.pathname}
                              asPath={router.asPath}
                              isSurveyTakingPage={isSurveyTakingPage}
                              profilePhanHeSyncing={profilePhanHeSyncing}
                              Component={Component}
                              pageProps={pageProps}
                            />
                          </UnsavedChangesProvider>
                        </Layout>
                      </AuthProvider>
                    </AppSignalRProvider>
                  )}
                  {!shouldUseLayout && isAuth && isOnDbRestoreLockPage && (
                    <AppSignalRProvider>
                      <Component {...pageProps} />
                    </AppSignalRProvider>
                  )}
                  {!shouldUseLayout && !(isAuth && isOnDbRestoreLockPage) && (
                    <Component {...pageProps} />
                  )}
                </div>
              </ConfigProvider>
            </ErrorBoundary>
          </PageTitleProvider>
        </AppLoadingProvider>
      </AppLocaleProvider>
    </RecoilRoot>
  );
}
